import { useEffect } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle2 } from "lucide-react";
import { FEATURES, featureBySlug } from "@/lib/features";
import { useSeo } from "@/lib/seo";
import { PublicFooter, PublicNav } from "@/components/home/PublicChrome";
import { FeatureMock } from "@/components/home/FeatureMock";
import { FinalCta } from "@/components/home/HomeSections";
import { Button } from "@/components/ui/button";

const Hero = ({ f }) => (
  <section className="bg-slate-900 pb-20 pt-32 sm:pt-40" data-testid="feature-hero">
    <div className="mx-auto grid max-w-6xl items-center gap-12 px-6 lg:grid-cols-2">
      <div>
        <Link to="/features" className="inline-flex items-center gap-1 text-sm text-slate-400 hover:text-white" data-testid="feature-back-link"><ArrowLeft className="h-4 w-4" /> All features</Link>
        <p className="home-rise font-data mt-6 flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-blue-400"><f.icon className="h-4 w-4" /> {f.title}</p>
        <h1 className="home-rise font-display mt-4 text-4xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-5xl" style={{ animationDelay: "80ms" }} data-testid="feature-title">{f.seoTitle}</h1>
        <p className="home-rise mt-6 max-w-xl text-base text-slate-300 sm:text-lg" style={{ animationDelay: "160ms" }} data-testid="feature-intro">{f.intro}</p>
        <Button asChild size="lg" className="home-rise mt-8 bg-blue-700 hover:bg-blue-800" style={{ animationDelay: "240ms" }} data-testid="feature-hero-signup"><Link to="/signup">Start your 14-day free trial <ArrowRight className="ml-1 h-4 w-4" /></Link></Button>
      </div>
      <div className="home-rise" style={{ animationDelay: "200ms" }}><FeatureMock kind={f.mock} /></div>
    </div>
  </section>
);

const Details = ({ f }) => (
  <section className="mx-auto grid max-w-6xl gap-8 px-6 pb-24 lg:grid-cols-2">
    <div data-testid="feature-benefits" className="rounded-xl border border-slate-200 bg-white p-8 shadow-xl">
      <h2 className="font-display text-2xl font-extrabold text-slate-900 sm:text-3xl">Why it helps</h2>
      <ul className="mt-6 space-y-3">
        {f.benefits.map((b) => <li key={b} className="flex gap-3 text-slate-700"><CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-blue-700" />{b}</li>)}
      </ul>
    </div>
    <div data-testid="feature-steps" className="rounded-xl border border-slate-200 bg-white p-8 shadow-xl">
      <h2 className="font-display text-2xl font-extrabold text-slate-900 sm:text-3xl">How it works</h2>
      <ol className="mt-6 space-y-6">
        {f.steps.map(([t, d], i) => (
          <li key={t} className="flex gap-4">
            <span className="font-display flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-blue-700 text-sm font-bold text-white">{i + 1}</span>
            <div><h3 className="font-semibold text-slate-900">{t}</h3><p className="mt-1 text-sm text-slate-600">{d}</p></div>
          </li>))}
      </ol>
    </div>
  </section>
);

const More = ({ slug }) => (
  <section className="border-t border-slate-800 bg-slate-950 py-16" data-testid="feature-more">
    <div className="mx-auto max-w-6xl px-6">
      <h2 className="font-display text-xl font-bold text-white">More features</h2>
      <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {FEATURES.filter((x) => x.slug !== slug).map((x) => (
          <Link key={x.slug} to={`/features/${x.slug}`} className="group flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-4 transition-colors hover:border-blue-600" data-testid={`feature-more-${x.slug}`}>
            <x.icon className="h-5 w-5 text-blue-700" /><span className="font-semibold text-slate-900">{x.title}</span><ArrowRight className="ml-auto h-4 w-4 text-slate-400 transition-transform group-hover:translate-x-1" />
          </Link>))}
      </div>
    </div>
  </section>
);

export default function FeaturePage() {
  const { slug } = useParams();
  const f = featureBySlug(slug);
  useEffect(() => { window.scrollTo(0, 0); }, [slug]);
  useSeo(f ? `${f.seoTitle} | Warrant Whizz` : "Warrant Whizz", f ? f.intro : "", `/features/${slug}`);
  if (!f) return <Navigate to="/features" replace />;
  return (
    <div className="min-h-screen bg-slate-900" data-testid={`feature-page-${slug}`}>
      <PublicNav />
      <main><Hero f={f} /><Details f={f} /><More slug={slug} /><FinalCta /></main>
      <PublicFooter />
    </div>
  );
}
